// TIBECO Cloud Backup & Sync
import React, { useState, useEffect } from 'react';
import {
  Server,
  Cloud,
  RefreshCw,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  Pause,
  Settings,
  Database,
  HardDrive,
  Zap,
  Shield,
  Download,
  Upload,
  Archive,
  History,
  FileText,
  Monitor,
  Smartphone,
  Laptop
} from 'lucide-react';

const CloudBackup = () => {
  const [backupStatus, setBackupStatus] = useState({
    isRunning: false,
    lastBackup: '2025-01-15 12:00:00',
    nextScheduled: '2025-01-16 00:00:00',
    totalSize: '23.7 GB',
    filesBackedUp: 1247,
    successRate: 99.2
  });

  const [backupJobs] = useState([
    {
      id: 1,
      name: 'Documente Contracte',
      type: 'documents',
      schedule: 'Zilnic la 00:00',
      lastRun: '2025-01-15 00:00',
      status: 'success',
      size: '5.2 GB',
      files: 234,
      retention: '90 zile',
      encrypted: true
    },
    {
      id: 2,
      name: 'Baza de Date TIBECO',
      type: 'database',
      schedule: 'La fiecare 6 ore',
      lastRun: '2025-01-15 12:00',
      status: 'running',
      size: '8.9 GB',
      files: 1,
      retention: '365 zile',
      encrypted: true
    },
    {
      id: 3,
      name: 'Fotografii Proiecte',
      type: 'media',
      schedule: 'Săptămânal Duminica',
      lastRun: '2025-01-12 02:00',
      status: 'success',
      size: '15.6 GB',
      files: 892,
      retention: '180 zile',
      encrypted: false
    },
    {
      id: 4,
      name: 'Sistem Operare',
      type: 'system',
      schedule: 'Lunar în prima zi',
      lastRun: '2025-01-01 03:00',
      status: 'warning',
      size: '45.3 GB',
      files: 12847,
      retention: '12 luni',
      encrypted: true
    }
  ]);

  const [syncDevices] = useState([
    {
      id: 1,
      name: 'PC Birou Adrian',
      type: 'desktop',
      status: 'online',
      lastSync: '2025-01-15 14:30',
      files: 1247,
      size: '12.3 GB'
    },
    {
      id: 2,
      name: 'Laptop Maria',
      type: 'laptop',
      status: 'online',
      lastSync: '2025-01-15 13:45',
      files: 892,
      size: '7.8 GB'
    },
    {
      id: 3,
      name: 'iPhone Mihai',
      type: 'mobile',
      status: 'offline',
      lastSync: '2025-01-14 18:20',
      files: 234,
      size: '2.1 GB'
    }
  ]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'running':
        return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'documents':
        return <FileText className="w-6 h-6 text-blue-600" />;
      case 'database':
        return <Database className="w-6 h-6 text-green-600" />;
      case 'media':
        return <Archive className="w-6 h-6 text-purple-600" />;
      case 'system':
        return <HardDrive className="w-6 h-6 text-gray-600" />;
      case 'desktop':
        return <Monitor className="w-6 h-6 text-blue-600" />;
      case 'laptop':
        return <Laptop className="w-6 h-6 text-green-600" />;
      case 'mobile':
        return <Smartphone className="w-6 h-6 text-purple-600" />;
      default:
        return <Server className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
      case 'online':
        return 'text-green-600';
      case 'running':
        return 'text-blue-600';
      case 'warning':
        return 'text-yellow-600';
      case 'error':
      case 'offline':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Backup & Sincronizare
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Gestionează backup-urile automate și sincronizarea datelor
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors">
                <Settings className="w-4 h-4 mr-2" />
                Configurare
              </button>
              <button className="flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors">
                <Play className="w-4 h-4 mr-2" />
                Backup Manual
              </button>
            </div>
          </div>
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 dark:bg-green-900 rounded-xl">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Ultimul Backup</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {backupStatus.lastBackup}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Următorul Backup</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {backupStatus.nextScheduled}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded-xl">
                <Database className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Mărime Totală</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {backupStatus.totalSize}
                </p>
                <p className="text-xs text-gray-400">{backupStatus.filesBackedUp} fișiere</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-xl">
                <Shield className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Rata de Succes</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">
                  {backupStatus.successRate}%
                </p>
                <p className="text-xs text-green-600">Excelent</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Backup Jobs */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Joburi Backup
                  </h3>
                  <button className="flex items-center px-3 py-1 bg-cyan-50 dark:bg-cyan-900 text-cyan-700 dark:text-cyan-300 rounded-lg text-sm font-medium hover:bg-cyan-100 dark:hover:bg-cyan-800">
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Actualizează
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {backupJobs.map((job) => (
                  <div key={job.id} className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-4">
                          {getTypeIcon(job.type)}
                        </div>
                        <div>
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                            {job.name}
                          </h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {job.schedule}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        {getStatusIcon(job.status)}
                        <span className={`ml-2 text-sm font-medium capitalize ${getStatusColor(job.status)}`}>
                          {job.status === 'running' ? 'În execuție' : 
                           job.status === 'success' ? 'Finalizat' :
                           job.status === 'warning' ? 'Avertisment' : 'Eroare'}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Ultima execuție</p>
                        <p className="font-medium text-gray-900 dark:text-white">{job.lastRun}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Mărime</p>
                        <p className="font-medium text-gray-900 dark:text-white">{job.size}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Fișiere</p>
                        <p className="font-medium text-gray-900 dark:text-white">{job.files}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 dark:text-gray-400">Păstrare</p>
                        <p className="font-medium text-gray-900 dark:text-white">{job.retention}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                      <div className="flex items-center space-x-2">
                        {job.encrypted && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            <Shield className="w-3 h-3 mr-1" />
                            Criptat
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="p-1 text-gray-400 hover:text-blue-600">
                          <Play className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-gray-600">
                          <Settings className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-green-600">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sync Devices & Stats */}
          <div className="space-y-6">
            {/* Synchronized Devices */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dispozitive Sincronizate
              </h3>
              
              <div className="space-y-4">
                {syncDevices.map((device) => (
                  <div key={device.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 mr-3">
                        {getTypeIcon(device.type)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {device.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {device.lastSync}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        device.status === 'online' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {device.status === 'online' ? 'Online' : 'Offline'}
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {device.size}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <button className="w-full mt-4 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 font-medium rounded-lg transition-colors">
                Adaugă Dispozitiv
              </button>
            </div>

            {/* Backup History */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Istoric Recent
                </h3>
                <History className="w-5 h-5 text-gray-400" />
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span>Backup complet</span>
                  </div>
                  <span className="text-gray-500">12:00</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <Upload className="w-4 h-4 text-blue-600 mr-2" />
                    <span>Sincronizare PC</span>
                  </div>
                  <span className="text-gray-500">11:45</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                    <span>Backup incremental</span>
                  </div>
                  <span className="text-gray-500">06:00</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
                    <span>Verificare integritate</span>
                  </div>
                  <span className="text-gray-500">02:30</span>
                </div>
              </div>
              
              <button className="w-full mt-4 text-cyan-600 hover:text-cyan-700 text-sm font-medium">
                Vezi toate activitățile
              </button>
            </div>

            {/* Storage Stats */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Statistici Stocare
              </h3>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600 dark:text-gray-400">Backup utilizat</span>
                    <span className="font-medium">23.7 GB / 100 GB</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-cyan-500 h-2 rounded-full" style={{ width: '23.7%' }} />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="font-semibold text-gray-900 dark:text-white">1,247</p>
                    <p className="text-gray-500 dark:text-gray-400">Fișiere</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <p className="font-semibold text-gray-900 dark:text-white">89</p>
                    <p className="text-gray-500 dark:text-gray-400">Foldere</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* S3 Configuration Status */}
        <div className="mt-8">
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20 rounded-xl border border-orange-200 dark:border-orange-800 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Zap className="w-6 h-6 text-orange-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Amazon S3 Backup Configuration
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Bucket: <span className="font-medium">tibeco-backup-ro</span> • 
                    Regiune: <span className="font-medium">eu-central-1</span> • 
                    Criptare: <span className="text-green-600 font-medium">AES-256</span> • 
                    Versioning: <span className="text-blue-600 font-medium">Activat</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Conectat
                </span>
                <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors">
                  Configurează
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CloudBackup;
